// Docs — the canonical-document download surface.
//
// Lists the curated canonical documents the public deploy ships
// (framework + reference manuals, the Coframe-QL manual + BNF, and the
// decisions ledger / ADRs) as download links. The list is served by the
// runtime at GET /docs-dl (a JSON manifest of files actually present on
// disk, with size + last-modified); each entry downloads from
// GET /docs-dl/{filename}.
//
// Served off the runtime root, so this uses the runtime base URL
// (VITE_RUNTIME_BASE_URL): "" in production (same origin), "/runtime" in
// dev (Vite proxies to the runtime FastAPI). The download surface is
// intentionally on the /docs-dl prefix — distinct from this client route
// /docs — so a hard refresh on /docs still serves the SPA shell.

import { useEffect, useState } from "react";

const RUNTIME_BASE =
  (import.meta.env?.VITE_RUNTIME_BASE_URL as string | undefined) ?? "/runtime";

interface DocEntry {
  label: string;
  filename: string;
  category: string;
  bytes: number;
  modified: number; // unix seconds
  href: string; // server-relative, e.g. "/docs-dl/coframe_framework_manual.md"
}

interface DocsManifest {
  docs: DocEntry[];
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(unixSeconds: number): string {
  try {
    return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function DocsPage() {
  const [docs, setDocs] = useState<DocEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${RUNTIME_BASE}/docs-dl`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as DocsManifest;
        if (!cancelled) setDocs(data.docs ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Preserve manifest order, but group under category headings.
  const categories: string[] = [];
  const byCategory = new Map<string, DocEntry[]>();
  for (const d of docs ?? []) {
    if (!byCategory.has(d.category)) {
      byCategory.set(d.category, []);
      categories.push(d.category);
    }
    byCategory.get(d.category)!.push(d);
  }

  return (
    <div style={pageStyle}>
      <h1 style={titleStyle}>Documentation</h1>
      <p style={leadStyle}>
        The canonical Coframe documents. These are the current source-of-truth
        manuals plus the decisions ledger (the ADRs — which may carry decisions
        newer than the manuals). Click to download the Markdown.
      </p>

      {error !== null && (
        <div style={errorStyle}>Couldn't load the document list: {error}</div>
      )}

      {docs === null && error === null && (
        <div style={mutedStyle}>Loading…</div>
      )}

      {docs !== null && docs.length === 0 && error === null && (
        <div style={mutedStyle}>No documents are available on this deploy.</div>
      )}

      {categories.map((cat) => (
        <section key={cat} style={sectionStyle}>
          <h2 style={catTitleStyle}>{cat}</h2>
          <ul style={listStyle}>
            {byCategory.get(cat)!.map((d) => (
              <li key={d.filename} style={itemStyle}>
                <a
                  href={`${RUNTIME_BASE}${d.href}`}
                  download={d.filename}
                  style={linkStyle}
                >
                  {d.label}
                </a>
                <span style={metaStyle}>
                  <code style={fileStyle}>{d.filename}</code>
                  {" · "}
                  {formatBytes(d.bytes)}
                  {" · updated "}
                  {formatDate(d.modified)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ── Inline styles — match the deliberately-minimal app shell. ──────────

const pageStyle: React.CSSProperties = {
  maxWidth: 760,
};
const titleStyle: React.CSSProperties = {
  fontSize: "1.4rem",
  fontWeight: 600,
  marginBottom: "0.35rem",
};
const leadStyle: React.CSSProperties = {
  color: "#555",
  fontSize: "0.9rem",
  lineHeight: 1.5,
  marginTop: 0,
  marginBottom: "1.5rem",
};
const sectionStyle: React.CSSProperties = {
  marginBottom: "1.5rem",
};
const catTitleStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#888",
  borderBottom: "1px solid #eee",
  paddingBottom: "0.3rem",
  marginBottom: "0.6rem",
};
const listStyle: React.CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.7rem",
};
const itemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
};
const linkStyle: React.CSSProperties = {
  color: "#0366d6",
  textDecoration: "none",
  fontSize: "0.95rem",
  fontWeight: 500,
};
const metaStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#999",
};
const fileStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#777",
};
const mutedStyle: React.CSSProperties = {
  color: "#999",
  fontSize: "0.9rem",
};
const errorStyle: React.CSSProperties = {
  color: "#a01b1b",
  background: "#fdecec",
  border: "1px solid #f3b6b6",
  borderRadius: 4,
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
  marginBottom: "1rem",
};
